import createCache from '@emotion/cache';
import isPropValid from '@emotion/is-prop-valid';
import { CacheProvider } from '@emotion/react';
import { DateTime } from '@ksyos/date-time';
import { EmotionCacheProvider } from '@ksyos/design-system';
import { Outlet, useLocation, useSearchParams } from '@ksyos/router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyleSheetManager } from 'styled-components';
import type { CustomWindow } from '@frontend/utils/types/customWindow';
import { Chat } from '@frontend/components/elements/Chat';
import { fetchserviceProviders } from '@frontend/store/serviceProviders/serviceProviderActions';
import '../assets/styles/global.css';
import { useDatadogView } from '@frontend/utils/hooks/useDatadogView';
import { SplitViewProvider } from '@frontend/utils/hooks/useSplitViewContext';
import type { IReduxState } from '../configureStore';
import { fetchAuthenticationAction } from '../store/authentication/actions';
import { setLanguage } from '../store/language/actions';
import { fetchServiceConsumers } from '../store/serviceConsumers/serviceConsumerActions';
import { Timeout } from '../store/Timeout';
import { setLanguageCode } from '../translate';
import { UiStateProvider } from './contexts/uiState/UiStateProvider';
import { AskEmploymentForSpecializationsModal } from './elements/askEmploymentForSpecializationsModal/AskEmploymentForSpecializationsModal';
import { CatchingComponent } from './elements/CatchingComponent';
import { Loader } from './elements/common';
import { Feedback } from './elements/Feedback';
import { Support } from './elements/Support';
import { MainNotLoggedIn } from './pages/notLoggedIn/MainNotLoggedIn';
import { MasterTemplate } from './templates/MasterTemplate';

const customWindow: CustomWindow = window;

// This is necessary for react-select to work properly with CSP: https://github.com/JedWatson/react-select/issues/4631
const cache = createCache({
    key: 'jss-class-prefix-key',
    nonce: customWindow.__webpack_nonce__,
});

export function App() {
    const {
        isChecking,
        loggedIn,
        isManager,
        selectedEmploymentId: employmentId,
    } = useSelector((state: IReduxState) => state.authentication);
    const { code: currentLanguage } = useSelector((state: IReduxState) => state.language);
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const dispatch = useDispatch();
    useDatadogView();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [location.pathname]);

    useEffect(() => {
        const language = searchParams.get('language');
        if (language) {
            setLanguage(language, true)(dispatch);
        }
    }, [searchParams, dispatch]);

    useEffect(() => {
        setLanguageCode(currentLanguage);
        DateTime.setDefaultLocale(currentLanguage);
    }, [currentLanguage]);

    useEffect(() => {
        fetchAuthenticationAction()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        if (loggedIn && !isManager && employmentId) {
            fetchServiceConsumers({ employmentId, isActive: true })(dispatch);
            fetchserviceProviders({ employmentId, isActive: true })(dispatch);
        }
    }, [employmentId, loggedIn, isManager, dispatch]);

    function showNavigationBar(pathname: string) {
        const isAddCarePathPage = /addCarePath/.test(pathname);
        const isAddRightsPage = /addRights/.test(pathname);
        const isSsoSelectEmploymentPage = /\/employments\/sso\/selectEmployment/.test(pathname);
        const isAccountRequestReviewPage = /\/accountRequest\/.*\/review/.test(pathname);
        const isOrganizationAddPage = /\/organizations\/add/.test(pathname);
        const isPractitionerEmploymentAddPage = /\/practitioners\/.*\/employments\/add/.test(pathname);
        const isOrganizationAddKsyosEmployeePage = /\/organizations\/.*\/view\/addEmployee/.test(pathname);
        const isSetMfaPage = /setMfa/.test(pathname);
        const isLoginSelectEmploymentPage = /selectEmployment/.test(pathname);

        const shouldHideNavigationBar =
            isAddCarePathPage ||
            isAddRightsPage ||
            isSsoSelectEmploymentPage ||
            isAccountRequestReviewPage ||
            isOrganizationAddPage ||
            isPractitionerEmploymentAddPage ||
            isOrganizationAddKsyosEmployeePage ||
            isSetMfaPage ||
            isLoginSelectEmploymentPage;

        if (shouldHideNavigationBar) {
            return false;
        }

        return true;
    }

    if (isChecking) {
        return (
            <div>
                <Loader
                    centered
                    large
                />
            </div>
        );
    }

    if (loggedIn === false) {
        return <MainNotLoggedIn />;
    }

    return (
        <UiStateProvider>
            <SplitViewProvider>
                <StyleSheetManager shouldForwardProp={shouldForwardProp}>
                    <Chat />
                    <Timeout />
                    <Feedback />
                    <Support />
                    <MasterTemplate navigation={showNavigationBar(location.pathname)}>
                        <CatchingComponent>
                            <CacheProvider value={cache}>
                                <EmotionCacheProvider>
                                    {employmentId && (
                                        <AskEmploymentForSpecializationsModal employmentId={employmentId} />
                                    )}
                                    <Outlet />
                                </EmotionCacheProvider>
                            </CacheProvider>
                        </CatchingComponent>
                    </MasterTemplate>
                </StyleSheetManager>
            </SplitViewProvider>
        </UiStateProvider>
    );
}

// This implements the default behavior from styled-components v5
function shouldForwardProp(propName: string, target: unknown) {
    if (typeof target === 'string') {
        // For HTML elements, forward the prop if it is a valid HTML attribute
        return isPropValid(propName);
    }

    // For other elements, forward all props
    return true;
}
